import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Calendar,
  Download,
  FileText,
  Loader2,
  MapPin,
  Play,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { API_URL } from "@/lib/api";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "");

export default function SummaryPage() {
  const { leadId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API_URL}/public/summary/${leadId}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Resumo não encontrado ou indisponível."));
  }, [leadId]);

  const fileUrl = (p) =>
    `${API_URL}/public/summary/${leadId}/file?path=${encodeURIComponent(p)}`;

  const FileGrid = ({ files, testPrefix }) => (
    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid={`${testPrefix}-grid`}>
      {files.map((f, i) => {
        const isImg = f.content_type?.startsWith("image/");
        const isVid = f.content_type?.startsWith("video/");
        return (
          <a
            key={f.path}
            href={fileUrl(f.path)}
            target="_blank"
            rel="noopener noreferrer"
            download={f.name}
            title={`${f.name} — abrir / baixar`}
            data-testid={`${testPrefix}-file-${i}`}
            className="group relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-50 transition-[border-color,box-shadow] duration-200 hover:border-brand hover:shadow-md"
          >
            {isImg ? (
              <img
                src={fileUrl(f.path)}
                alt={f.name}
                className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
              />
            ) : isVid ? (
              <>
                <video src={fileUrl(f.path)} muted preload="metadata" className="h-full w-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center bg-petrol/30">
                  <Play className="h-6 w-6 text-white" fill="currentColor" />
                </span>
              </>
            ) : (
              <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-slate-500">
                <FileText className="h-6 w-6" />
                <span className="w-full truncate text-center text-[0.65rem]">{f.name}</span>
              </span>
            )}
            <span className="absolute bottom-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-petrol shadow-sm transition-colors group-hover:bg-brand group-hover:text-white">
              <Download className="h-3 w-3" />
            </span>
          </a>
        );
      })}
    </div>
  );

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ice px-6 text-center" data-testid="summary-error">
        <Logo />
        <p className="text-sm font-medium text-slate-500">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ice" data-testid="summary-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ice" data-testid="summary-page">
      <header className="border-b border-slate-200/70 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Logo size={30} dataTestId="summary-logo" />
          <span className="font-mono text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-slate-400">
            Resumo do atendimento
          </span>
        </div>
      </header>

      <section className="bg-petrol text-white">
        <div className="mx-auto max-w-3xl px-5 py-10">
          <p className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-brand-400">
            Resolv · Pós-obra
          </p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight" data-testid="summary-title">
            {data.service_type || "Atendimento Resolv"}
          </h1>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
            <span className="inline-flex items-center gap-1.5" data-testid="summary-client">
              <Wrench className="h-3.5 w-3.5 text-brand" /> {data.client_name}
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="summary-city">
              <MapPin className="h-3.5 w-3.5 text-brand" /> {data.city}
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="summary-date">
              <Calendar className="h-3.5 w-3.5 text-brand" /> Aberto em {fmtDate(data.created_at)}
            </span>
          </div>
          {data.address && (
            <p className="mt-3 text-xs text-white/50" data-testid="summary-address">
              {data.address}
            </p>
          )}
        </div>
      </section>

      <main className="mx-auto max-w-3xl space-y-5 px-5 py-8">
        <section className="rounded-2xl border border-slate-200/80 bg-white p-5" data-testid="summary-problem">
          <h2 className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-brand">
            O problema relatado
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">{data.message}</p>
          {data.attachments?.length > 0 && <FileGrid files={data.attachments} testPrefix="summary-attachments" />}
        </section>

        {(data.diagnosis || data.solution) && (
          <section className="grid gap-5 sm:grid-cols-2" data-testid="summary-diagnosis-solution">
            {data.diagnosis && (
              <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
                <h2 className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-brand">
                  Diagnóstico do problema
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-700" data-testid="summary-diagnosis">
                  {data.diagnosis}
                </p>
              </div>
            )}
            {data.solution && (
              <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
                <h2 className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-brand">
                  Solução
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-700" data-testid="summary-solution">
                  {data.solution}
                </p>
              </div>
            )}
          </section>
        )}

        {(data.deadline || data.warranty) && (
          <section className="flex flex-wrap gap-3" data-testid="summary-terms">
            {data.deadline && (
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600" data-testid="summary-deadline">
                <Calendar className="h-3.5 w-3.5 text-brand" /> Prazo: {data.deadline}
              </span>
            )}
            {data.warranty && (
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600" data-testid="summary-warranty">
                <ShieldCheck className="h-3.5 w-3.5 text-brand" /> Garantia: {data.warranty}
              </span>
            )}
          </section>
        )}

        {data.final_price != null && (
          <section className="overflow-hidden rounded-2xl bg-petrol" data-testid="summary-price">
            <div className="h-1 bg-brand" />
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5">
              <div>
                <p className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-white/50">
                  Valor total do serviço
                </p>
                <p className="mt-1 font-display text-3xl font-extrabold tracking-tight text-white" data-testid="summary-final-price">
                  {brl.format(data.final_price)}
                </p>
              </div>
              <p className="text-[0.65rem] text-white/40">Valor final, já com tudo incluso.</p>
            </div>
          </section>
        )}

        {data.before?.length > 0 && (
          <section className="rounded-2xl border border-slate-200/80 bg-white p-5" data-testid="summary-before">
            <h2 className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-brand">
              Fotos — antes da obra
            </h2>
            <FileGrid files={data.before} testPrefix="summary-before" />
          </section>
        )}

        {data.after?.length > 0 && (
          <section className="rounded-2xl border border-slate-200/80 bg-white p-5" data-testid="summary-after">
            <h2 className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-brand">
              Fotos — depois da obra
            </h2>
            <FileGrid files={data.after} testPrefix="summary-after" />
          </section>
        )}

        {data.invoice?.length > 0 && (
          <section className="rounded-2xl border border-slate-200/80 bg-white p-5" data-testid="summary-invoice">
            <h2 className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-brand">
              Nota fiscal do serviço
            </h2>
            <ul className="mt-3 space-y-2">
              {data.invoice.map((f, i) => (
                <li key={f.path}>
                  <a
                    href={fileUrl(f.path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={f.name}
                    data-testid={`summary-invoice-file-${i}`}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 transition-colors hover:border-brand hover:bg-brand/5"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-brand" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700">{f.name}</span>
                    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold text-brand">
                      <Download className="h-3.5 w-3.5" /> Baixar
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      <footer className="border-t border-slate-200/70 bg-white">
        <div className="mx-auto max-w-3xl px-5 py-5 text-center">
          <p className="text-[0.65rem] text-slate-400">
            Resolv · Gestão de demandas imobiliárias · CNPJ 69.470.510/0001-29 · WhatsApp (48) 99122-0068
          </p>
        </div>
      </footer>
    </div>
  );
}
